import { useState } from "react";
import type {
  AddVersionsOutcome,
  AddVersionsTarget,
  StagedVersionItem,
} from "../../types/addVersions";
import type { VersionDialogView } from "../../hooks/useAddNewVersionsDialog";
import type { DetachVersionResult } from "@/stores/documents/repository";
import { AddNewVersionsDialog } from "./AddNewVersionsDialog";
import { VersioningDialog } from "./VersioningDialog";

/** The parts of `useAddNewVersionsDialog` the two dialogs read. */
interface VersionDialogsState {
  view: VersionDialogView;
  cameFromVersioning: boolean;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  confirm: (items: StagedVersionItem[]) => Promise<AddVersionsOutcome>;
  showAdd: () => void;
  replaceWithAdd: () => void;
  retarget: (moved: DetachVersionResult) => void;
  showVersioning: () => void;
  onVersionsEdited: (versionGroups: string[]) => void;
}

interface VersionDialogsProps {
  state: VersionDialogsState;
  /** The targeted document, or null when nothing is open. */
  target: AddVersionsTarget | null;
  buildingId: string;
  /** The host's version preview, as its version history uses. */
  onPreviewVersion?: (documentId: string, filePath: string, filename: string) => void;
}

/**
 * The version dialogs a documents view hosts: "Versioning" for a document
 * that already has versions, "Add new versions" for one that has not — and
 * for the former once the user chooses to add more. One host for the list and
 * the timeline, so both switch between the two the same way.
 */
export function VersionDialogs({
  state,
  target,
  buildingId,
  onPreviewVersion,
}: VersionDialogsProps) {
  const open = target !== null;
  // Files staged in "Add new versions" outlive a trip back to Versioning; a
  // save there returns to them rather than closing, which would drop them.
  const [stagedCount, setStagedCount] = useState(0);
  return (
    <>
      <VersioningDialog
        open={open && state.view === "versioning"}
        onOpenChange={state.onOpenChange}
        target={
          target
            ? { id: target.id, filename: target.filename, versionGroup: target.versionGroup }
            : null
        }
        onAddVersions={state.showAdd}
        onNothingToArrange={state.replaceWithAdd}
        onEdited={state.onVersionsEdited}
        onPreview={onPreviewVersion}
        hasStagedFiles={stagedCount > 0}
        onSaved={
          stagedCount > 0
            ? ({ targetDetached }) => {
                if (targetDetached) state.retarget(targetDetached);
                state.showAdd();
              }
            : undefined
        }
      />
      <AddNewVersionsDialog
        open={open && state.view === "add"}
        onOpenChange={state.onOpenChange}
        buildingId={buildingId}
        targetDocument={target}
        onConfirm={state.confirm}
        isSubmitting={state.isSubmitting}
        onBack={state.cameFromVersioning ? state.showVersioning : undefined}
        onStagedChange={setStagedCount}
      />
    </>
  );
}
